"use client";

import { useEffect } from "react";
import { motion, useMotionValue, animate, useIsPresent } from "motion/react";

interface GradientOverlayProps {
  size: { width: number; height: number };
  variant?: 'default' | 'subtle';
}

export function GradientOverlay({ size, variant = 'default' }: GradientOverlayProps) {
  const breathe = useMotionValue(0);
  const isPresent = useIsPresent();

  useEffect(() => {
    if (!isPresent) {
      animate(breathe, 0, { duration: 0.5, ease: "easeInOut" });
    }

    async function playBreathingAnimation() {
      await animate(breathe, 1, {
        duration: 0.5,
        delay: 0.35,
        ease: [0, 0.55, 0.45, 1],
      });

      animate(breathe, [null, 0.7, 1], {
        duration: 15,
        repeat: Infinity,
        repeatType: "loop",
        ease: "easeInOut",
      });
    }

    playBreathingAnimation();
  }, [isPresent]);

  const enterDuration = 0.75;
  const exitDuration = 0.5;

  const isLandscape = size.width > size.height;
  const shortEdge = Math.min(size.width, size.height);
  const expandingCircleRadius = shortEdge / 3;
  const gradientSize = isLandscape ? size.height * 1.5 : size.width * 2;
  const topLeftOffset = isLandscape ? -gradientSize * 0.6 : -size.width;

  const topOpacity = variant === 'subtle' ? (isLandscape ? 0.5 : 0.65) : (isLandscape ? 0.7 : 0.9);
  const bottomOpacity = variant === 'subtle' ? (isLandscape ? 0.5 : 0.65) : (isLandscape ? 0.7 : 0.9);
  const circleScale = variant === 'subtle' ? 8 : 10;

  return (
    <div className="gradient-container">
      <motion.div
        className="expanding-circle"
        initial={{
          scale: 0,
          opacity: 1,
          backgroundColor: variant === 'subtle' ? "rgb(229, 231, 235)" : "rgb(221, 214, 254)",
        }}
        animate={{
          scale: circleScale,
          opacity: 0.2,
          backgroundColor: variant === 'subtle' ? "rgb(167, 139, 250)" : "rgb(147, 51, 234)",
          transition: {
            duration: enterDuration,
            opacity: { duration: enterDuration, ease: "easeInOut" },
          },
        }}
        exit={{
          scale: 0,
          opacity: 1,
          backgroundColor: "rgb(221, 214, 254)",
          transition: { duration: exitDuration },
        }}
        style={{
          left: `calc(50% - ${expandingCircleRadius / 2}px)`,
          top: "100%",
          width: expandingCircleRadius,
          height: expandingCircleRadius,
          originX: 0.5,
          originY: 1,
        }}
      />

      <motion.div
        className="gradient-circle top-left"
        initial={{ opacity: 0 }}
        animate={{
          opacity: topOpacity,
          transition: { duration: enterDuration },
        }}
        exit={{
          opacity: 0,
          transition: { duration: exitDuration },
        }}
        style={{
          scale: breathe,
          width: gradientSize,
          height: gradientSize,
          top: topLeftOffset,
          left: isLandscape ? -gradientSize * 0.3 : -size.width,
        }}
      />

      <motion.div
        className="gradient-circle bottom-right"
        initial={{ opacity: 0 }}
        animate={{
          opacity: bottomOpacity,
          transition: { duration: enterDuration },
        }}
        exit={{
          opacity: 0,
          transition: { duration: exitDuration },
        }}
        style={{
          scale: breathe,
          width: gradientSize,
          height: gradientSize,
          bottom: isLandscape ? -gradientSize * 0.6 : -size.width,
          right: isLandscape ? -gradientSize * 0.3 : -size.width,
          top: isLandscape ? undefined : size.height - size.width,
          left: isLandscape ? undefined : 0,
        }}
      />
    </div>
  );
}
